import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { normalizeSectionId } from '@theme/section-renderer';
import { OverflowList } from '@theme/overflow-list';
import { yieldToMainThread, getViewParameterValue, ResizeNotifier } from '@theme/utilities';

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets - The fieldset elements.
 * @property {HTMLElement} [overflowList] - The overflow list element.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  /** @type {Set<number>} */
  #userClickedFieldsets = new Set();

  /** @type {number | undefined} */
  #lastChangedFieldsetIndex;

  /** @type {Array<{id: number, available: boolean, option1: string | null, option2: string | null, option3: string | null}> | null} */
  #variantsCatalog = null;

  #resizeObserver = new ResizeNotifier(() => this.updateVariantPickerCss());

  connectedCallback() {
    super.connectedCallback();
    this.#sortSizeOptions();
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex((radio) => radio.dataset.currentChecked === 'true');
      if (initialCheckedIndex !== -1) {
        this.#checkedIndices.push([initialCheckedIndex]);
      }
    });

    this.addEventListener('change', this.variantChanged.bind(this));
    this.#disableUnavailableInputs();
    this.ensureSelectableSelections();
    this.#resizeObserver.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#sortSizeOptions();
    this.#syncRadiosFromFieldsets();
    this.#disableUnavailableInputs();
    this.ensureSelectableSelections();
  }

  /**
   * Re-orders size pills (call after external DOM updates such as quick-add morph).
   */
  sortSizeOptions() {
    this.#sortSizeOptions();
  }

  /**
   * Ensures checked options are in stock; disables sold-out inputs.
   * @returns {boolean} Whether the selection was corrected.
   */
  ensureSelectableSelections() {
    this.#disableUnavailableInputs();

    let changed = false;
    const fieldsets = this.#getSortableFieldsets();

    fieldsets.forEach((fieldset, fieldsetIndex) => {
      const select = fieldset.querySelector('select.variant-option__select');
      if (select instanceof HTMLSelectElement) {
        if (this.#ensureSelectableSelectOption(select)) {
          changed = true;
        }
        return;
      }

      const radios = Array.from(fieldset.querySelectorAll('input[type="radio"]'));
      if (radios.length === 0) return;

      this.#radios[fieldsetIndex] = radios;

      const checked = radios.find((radio) => radio.checked);
      if (checked && !this.#isOptionAvailable(checked)) {
        const firstAvailable = radios.find((radio) => this.#isOptionAvailable(radio));
        if (firstAvailable) {
          if (checked instanceof HTMLInputElement) {
            checked.checked = false;
            checked.dataset.currentChecked = 'false';
          }
          firstAvailable.checked = true;
          this.updateSelectedOption(firstAvailable);
          changed = true;
        }
      }
    });

    if (changed) {
      this.#dispatchSelectionChange();
    }

    return changed;
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    if (!this.#isOptionAvailable(selectedOption)) {
      event.preventDefault();
      this.#revertUnavailableSelection(event.target);
      return;
    }

    if (this.closest('quick-add-dialog')) {
      this.#previewQuickAddModalImage(selectedOption);
    }

    this.updateSelectedOption(event.target);

    const changedFieldsetIndex = this.#compactFieldsetIndex(selectedOption.closest('fieldset'));
    if (changedFieldsetIndex !== -1) {
      this.#userClickedFieldsets.add(changedFieldsetIndex);
      this.#lastChangedFieldsetIndex = changedFieldsetIndex;
    }

    this.dispatchEvent(new VariantSelectedEvent({
      id: selectedOption.dataset.optionValueId ?? '',
    }));

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;
    const isOnFeaturedProductSection = Boolean(this.closest('featured-product-information'));

    const morphElementSelector = loadsNewProduct
      ? 'main'
      : isOnFeaturedProductSection
      ? 'featured-product-information'
      : undefined;

    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), morphElementSelector);

    const url = new URL(window.location.href);

    const variantId = selectedOption.dataset.variantId || null;

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * @typedef {object} FieldsetMeasurements
   * @property {HTMLFieldSetElement} fieldset
   * @property {number | undefined} currentIndex
   * @property {number | undefined} previousIndex
   * @property {number | undefined} currentWidth
   * @property {number | undefined} previousWidth
   */

  /**
   * Gets measurements for a single fieldset (read phase).
   * @param {number} fieldsetIndex
   * @returns {FieldsetMeasurements | null}
   */
  #getFieldsetMeasurements(fieldsetIndex) {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    const fieldset = fieldsets[fieldsetIndex];
    const checkedIndices = this.#checkedIndices[fieldsetIndex];
    const radios = this.#radios[fieldsetIndex];

    if (!radios || !checkedIndices || !fieldset) return null;

    const [currentIndex, previousIndex] = checkedIndices;

    return {
      fieldset,
      currentIndex,
      previousIndex,
      currentWidth: currentIndex !== undefined ? radios[currentIndex]?.parentElement?.offsetWidth : undefined,
      previousWidth: previousIndex !== undefined ? radios[previousIndex]?.parentElement?.offsetWidth : undefined,
    };
  }

  /**
   * Applies measurements to a fieldset (write phase).
   * @param {FieldsetMeasurements} measurements
   */
  #applyFieldsetMeasurements({ fieldset, currentWidth, previousWidth, currentIndex, previousIndex }) {
    if (currentWidth) {
      fieldset.style.setProperty('--pill-width-current', `${currentWidth}px`);
    } else if (currentIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-current');
    }

    if (previousWidth) {
      fieldset.style.setProperty('--pill-width-previous', `${previousWidth}px`);
    } else if (previousIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-previous');
    }
  }

  /**
   * Updates the fieldset CSS.
   * @param {number} fieldsetIndex - The fieldset index.
   */
  updateFieldsetCss(fieldsetIndex) {
    if (Number.isNaN(fieldsetIndex)) return;

    const measurements = this.#getFieldsetMeasurements(fieldsetIndex);
    if (measurements) {
      this.#applyFieldsetMeasurements(measurements);
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = this.#compactFieldsetIndex(target.closest('fieldset'));
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (fieldsetIndex !== -1 && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          // Clear previous checked states
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          // Update checked indices array - keep only the last 2 selections
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          // Update the new states
          const newCurrentIndex = checkedIndices[0]; // This is always inputIndex
          const newPreviousIndex = checkedIndices[1]; // This might be undefined

          // newCurrentIndex is guaranteed to exist since we just added it
          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
          }

          this.updateFieldsetCss(fieldsetIndex);
        }
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();

    // preserve view parameter, if it exists, for alternative product view testing
    if (viewParamValue) params.push(`view=${viewParamValue}`);

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of some specific sections, we need to append section_id=xxxx to the URL
    const pdpSectionEl = this.closest('.shopify-section.ea-pdp-section');
    if (pdpSectionEl?.id) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=${normalizeSectionId(pdpSectionEl.id)}&${params.join('&')}`;
    }

    const SECTION_ID_MAP = {
      'quick-add-component': 'section-rendering-product-card',
      'quick-add-dialog': 'quick-add-rendering',
      'swatches-variant-picker-component': 'section-rendering-product-card',
      'featured-product-information': this.closest('featured-product-information')?.id,
    };

    const closestSectionId = /** @type {keyof typeof SECTION_ID_MAP} | undefined */ (
      Object.keys(SECTION_ID_MAP).find((sectionId) => this.closest(sectionId))
    );

    if (closestSectionId) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=${SECTION_ID_MAP[closestSectionId]}&${params.join('&')}`;
    }

    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {string} [morphElementSelector] - The selector of the element to be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, morphElementSelector) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (textContent == null) return;

        let variantData = null;
        if (textContent !== 'null') {
          try {
            variantData = JSON.parse(textContent);
          } catch {
            variantData = null;
          }
        }

        let newProduct;

        if (morphElementSelector === 'main') {
          this.updateMain(html);
          if (!variantData?.id || !this.#hasCompleteSelection()) {
            variantData = null;
          }
        } else if (morphElementSelector) {
          this.updateElement(html, morphElementSelector);
          if (!variantData?.id || !this.#hasCompleteSelection()) {
            variantData = null;
          }
        } else {
          const { overflowList } = this.refs;
          const wasSwatchesExpanded =
            overflowList instanceof OverflowList && overflowList.getAttribute('disabled') === 'true';

          newProduct = this.updateVariantPicker(html);

          if (wasSwatchesExpanded) {
            const overflowListAfterMorph = overflowList;
            if (overflowListAfterMorph instanceof OverflowList) {
              overflowListAfterMorph.showAll();
            }
          }

          if (!variantData?.id || !this.#hasCompleteSelection()) {
            variantData = null;
          }
        }

        // Dispatch for all paths so product-form-component can reset #variantChangeInProgress
        if (this.selectedOptionId) {
          this.dispatchEvent(
            new VariantUpdateEvent(variantData, this.selectedOptionId, {
              html,
              productId: this.dataset.productId ?? '',
              newProduct,
            })
          );
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
          if (this.selectedOptionId) {
            this.dispatchEvent(
              new VariantUpdateEvent(null, this.selectedOptionId, {
                productId: this.dataset.productId ?? '',
              })
            );
          }
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document | Element} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = this.#findVariantPickerInResponse(newHtml);

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource, {
      ...MORPH_OPTIONS,
      getNodeKey: (node) => {
        if (!(node instanceof HTMLElement)) return undefined;
        const key = node.dataset.key;
        return key;
      },
    });
    this.updateVariantPickerCss();
    this.#sortSizeOptions();
    this.#syncRadiosFromFieldsets();
    this.#disableUnavailableInputs();
    this.#reconcilePdpSelectionsAfterMorph();
    this.#syncCheckedAttributes();
    this.ensureSelectableSelections();

    return newProduct;
  }

  /**
   * @param {Document | Element} newHtml
   * @returns {Element | null}
   */
  #findVariantPickerInResponse(newHtml) {
    const tag = this.tagName.toLowerCase();

    if (!this.closest('quick-add-dialog')) {
      return newHtml.querySelector(tag);
    }

    const gridRoot =
      newHtml instanceof Element && newHtml.matches('[data-product-grid-content]')
        ? newHtml
        : newHtml.querySelector('[data-product-grid-content]');

    return gridRoot?.querySelector(tag) ?? newHtml.querySelector(tag);
  }

  updateVariantPickerCss() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    // Batch all reads first across all fieldsets to avoid layout thrashing
    const measurements = fieldsets.map((_, index) => this.#getFieldsetMeasurements(index)).filter((m) => m !== null);

    // Batch all writes after all reads
    for (const measurement of measurements) {
      this.#applyFieldsetMeasurements(measurement);
    }
  }

  /**
   * Re-renders the desired element.
   * @param {Document} newHtml - The new HTML.
   * @param {string} elementSelector - The selector of the element to re-render.
   */
  updateElement(newHtml, elementSelector) {
    const element = this.closest(elementSelector);
    const newElement = newHtml.querySelector(elementSelector);

    if (!element || !newElement) {
      throw new Error(`No new element source found for ${elementSelector}`);
    }

    morph(element, newElement);
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      return undefined;
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions
      .map((option) => option.dataset.optionValueId)
      .filter((id) => Boolean(id));
  }

  /** @type {readonly string[]} */
  static #SIZE_RANK = [
    'xxs',
    'xs',
    's',
    'm',
    'l',
    'xl',
    'xxl',
    '2xl',
    '3xl',
    'xxxl',
    '4xl',
    '5xl',
    'one size',
    'os',
    'free size',
  ];

  /**
   * @param {string} name
   */
  #sizeRankIndex(name) {
    const normalized = name.trim().toLowerCase().split(' - ')[0] ?? '';
    const rank = VariantPicker.#SIZE_RANK.indexOf(normalized);
    if (rank !== -1) return rank;

    const numeric = parseFloat(normalized);
    if (!Number.isNaN(numeric)) return 100 + numeric;

    return 1000;
  }

  /**
   * @param {HTMLElement} container
   */
  #getLegendOptionName(container) {
    const legend = container.querySelector('legend, .variant-option__dropdown-label label');
    if (!legend) return '';

    const primary = legend.querySelector('.pdp-variant-legend-text');
    const rawText = primary?.textContent ?? legend.textContent;
    const normalized = rawText?.trim().toLowerCase() ?? '';
    const firstToken = normalized.split(/\s+/)[0]?.replace(/:$/, '') ?? '';

    return firstToken;
  }

  /**
   * @param {HTMLElement} container
   */
  #isSizeOptionContainer(container) {
    const name = this.#getLegendOptionName(container);
    return name === 'size' || name === 'ukuran';
  }

  /**
   * @param {HTMLElement} label
   */
  #getSizeOptionLabelText(label) {
    const input = label.querySelector('input[type="radio"], input');

    if (input instanceof HTMLInputElement) {
      const value = input.value?.trim();
      if (value) return value;

      const aria = input.getAttribute('aria-label')?.trim();
      if (aria) return aria.split(' - ')[0]?.trim() ?? aria;
    }

    return (
      label.querySelector('[data-key="variant-option-text"]')?.textContent?.trim() ??
      label.textContent?.trim() ??
      ''
    );
  }

  /**
   * @returns {HTMLFieldSetElement[]}
   */
  #getSortableFieldsets() {
    const fromRefs = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    if (fromRefs.length > 0) return fromRefs;

    return Array.from(this.querySelectorAll('fieldset.variant-option'));
  }

  /**
   * Converts a fieldset element into the compact DOM-order index used by
   * #radios/#checkedIndices/this.refs.fieldsets — NOT the same as its
   * data-fieldset-index (the true, possibly-sparse product-option position;
   * a single-value option renders as static text with no fieldset at all).
   * @param {Element | null} fieldset
   * @returns {number} -1 if not found among the sortable fieldsets.
   */
  #compactFieldsetIndex(fieldset) {
    return this.#getSortableFieldsets().indexOf(/** @type {HTMLFieldSetElement} */ (fieldset));
  }

  #sortSizeOptions() {
    const fieldsets = this.#getSortableFieldsets();

    fieldsets.forEach((fieldset) => {
      if (!this.#isSizeOptionContainer(fieldset)) return;

      const labels = Array.from(fieldset.querySelectorAll('.variant-option__button-label'));
      labels
        .sort((a, b) => {
          const labelA = /** @type {HTMLElement} */ (a);
          const labelB = /** @type {HTMLElement} */ (b);
          return (
            this.#sizeRankIndex(this.#getSizeOptionLabelText(labelA)) -
            this.#sizeRankIndex(this.#getSizeOptionLabelText(labelB))
          );
        })
        .forEach((label) => fieldset.appendChild(label));
    });

    this.querySelectorAll('.variant-option--dropdowns').forEach((container) => {
      if (!(container instanceof HTMLElement) || !this.#isSizeOptionContainer(container)) return;

      const select = container.querySelector('select');
      if (!(select instanceof HTMLSelectElement)) return;

      Array.from(select.options)
        .sort((a, b) => this.#sizeRankIndex(a.text) - this.#sizeRankIndex(b.text))
        .forEach((option) => select.appendChild(option));
    });
  }

  #syncRadiosFromFieldsets() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    fieldsets.forEach((fieldset, index) => {
      if (!this.#radios[index]) return;
      this.#radios[index] = Array.from(fieldset.querySelectorAll('input'));
    });
  }

  /**
   * @param {HTMLElement} option
   */
  #isOptionAvailable(option) {
    if (option instanceof HTMLOptionElement) {
      return option.disabled !== true && option.dataset.optionAvailable !== 'false';
    }

    if (option instanceof HTMLInputElement) {
      return option.disabled !== true && option.dataset.optionAvailable !== 'false';
    }

    return true;
  }

  #disableUnavailableInputs() {
    this.querySelectorAll('input[data-option-available="false"]').forEach((input) => {
      if (input instanceof HTMLInputElement) {
        input.disabled = true;
      }
    });

    this.querySelectorAll('option[data-option-available="false"]').forEach((option) => {
      if (option instanceof HTMLOptionElement) {
        option.disabled = true;
      }
    });
  }

  /**
   * @returns {boolean}
   */
  #isPdpVariantPicker() {
    return (
      this.dataset.templateProductMatch === 'true' &&
      !this.closest('product-card') &&
      !this.closest('quick-add-dialog')
    );
  }

  /**
   * @param {HTMLElement} fieldset
   * @returns {boolean}
   */
  #isColorFieldset(fieldset) {
    return fieldset.classList.contains('variant-option--ea-cards');
  }

  /**
   * Clears size selections that Shopify implied from a colour-only pick.
   */
  #reconcilePdpSelectionsAfterMorph() {
    if (!this.#isPdpVariantPicker() || this.#lastChangedFieldsetIndex === undefined) return;

    const fieldsets = this.#getSortableFieldsets();
    const changedFieldset = fieldsets[this.#lastChangedFieldsetIndex];
    if (!changedFieldset || !this.#isColorFieldset(changedFieldset)) return;

    fieldsets.forEach((fieldset, index) => {
      if (!this.#isSizeOptionContainer(fieldset)) return;
      if (this.#userClickedFieldsets.has(index)) return;

      const radios = Array.from(fieldset.querySelectorAll('input[type="radio"]'));
      for (const radio of radios) {
        if (!(radio instanceof HTMLInputElement)) continue;
        radio.checked = false;
        radio.dataset.currentChecked = 'false';
        radio.dataset.previousChecked = 'false';
      }

      this.#checkedIndices[index] = [];
      fieldset.style.removeProperty('--pill-width-current');
      fieldset.style.removeProperty('--pill-width-previous');
    });
  }

  /**
   * Keeps pill fill in sync when morph only updates the native :checked state.
   */
  #syncCheckedAttributes() {
    const fieldsets = this.#getSortableFieldsets();

    fieldsets.forEach((fieldset, fieldsetIndex) => {
      const radios = Array.from(fieldset.querySelectorAll('input[type="radio"]'));
      let checkedIndex = -1;

      radios.forEach((radio, inputIndex) => {
        if (!(radio instanceof HTMLInputElement)) return;

        if (radio.checked) {
          radio.dataset.currentChecked = 'true';
          radio.dataset.previousChecked = 'false';
          checkedIndex = inputIndex;
        } else {
          radio.dataset.currentChecked = 'false';
          radio.dataset.previousChecked = 'false';
        }
      });

      this.#radios[fieldsetIndex] = radios;

      if (checkedIndex !== -1) {
        this.#checkedIndices[fieldsetIndex] = [checkedIndex];
        this.updateFieldsetCss(fieldsetIndex);
        return;
      }

      this.#checkedIndices[fieldsetIndex] = [];
      fieldset.style.removeProperty('--pill-width-current');
      fieldset.style.removeProperty('--pill-width-previous');
    });
  }

  /**
   * Whether every option row has an explicit user selection.
   * @returns {boolean}
   */
  hasCompleteSelection() {
    return this.#hasCompleteSelection();
  }

  /**
   * @param {{
   *   quantity_rule?: { max?: number | null },
   *   inventory_management?: string,
   *   inventory_policy?: string,
   *   inventory_quantity?: number
   * }} variant
   * @returns {number | null}
   */
  #getPurchaseMaxForVariant(variant) {
    let max = variant.quantity_rule?.max ?? null;

    if (variant.inventory_management === 'shopify' && variant.inventory_policy === 'deny') {
      const inventoryCap = variant.inventory_quantity;
      if (typeof inventoryCap === 'number' && (max === null || inventoryCap < max)) {
        max = inventoryCap;
      }
    }

    return max;
  }

  /**
   * @returns {{
   *   id: number,
   *   available: boolean,
   *   option1: string | null,
   *   option2: string | null,
   *   option3: string | null,
   *   quantity_rule?: { max?: number | null },
   *   inventory_management?: string,
   *   inventory_policy?: string,
   *   inventory_quantity?: number
   * } | null}
   */
  #resolveFullSelectedVariant() {
    if (!this.#hasCompleteSelection()) return null;

    const variants = this.#loadVariantsCatalog();
    if (!variants?.length) return null;

    const selected = this.#getSelectedOptionValuesByPosition();
    if (selected.some(({ value }) => !value)) return null;

    // Positions with no fieldset (single-value options rendered as static text)
    // are constant across every variant of this product, so they're safely
    // excluded from matching here rather than needing a value to compare.
    const optionKeys = /** @type {const} */ (['option1', 'option2', 'option3']);

    return (
      variants.find((variant) =>
        selected.every(({ position, value }) => variant[optionKeys[position]] === value)
      ) ?? null
    );
  }

  /**
   * @param {number} cartQuantity
   * @param {number} [quantityToAdd=1]
   * @returns {{canAdd: boolean, maxQuantity: number | null}}
   */
  canAddVariantToCart(cartQuantity, quantityToAdd = 1) {
    const variant = this.#resolveFullSelectedVariant();
    if (!variant?.id) {
      return { canAdd: false, maxQuantity: null };
    }

    const maxQuantity = this.#getPurchaseMaxForVariant(variant);
    if (maxQuantity === null) {
      return { canAdd: true, maxQuantity: null };
    }

    return {
      canAdd: cartQuantity + quantityToAdd <= maxQuantity,
      maxQuantity,
    };
  }

  /**
   * Resolves the matching variant from the current DOM selection (no network).
   * @returns {{id: number, available: boolean} | null}
   */
  resolveSelectedVariant() {
    const variant = this.#resolveFullSelectedVariant();
    if (!variant?.id) return null;

    return { id: variant.id, available: variant.available !== false };
  }

  /**
   * @returns {Array<{
   *   id: number,
   *   available: boolean,
   *   option1: string | null,
   *   option2: string | null,
   *   option3: string | null,
   *   quantity_rule?: { max?: number | null },
   *   inventory_management?: string,
   *   inventory_policy?: string,
   *   inventory_quantity?: number
   * }> | null}
   */
  #loadVariantsCatalog() {
    if (this.#variantsCatalog) return this.#variantsCatalog;

    const script = this.querySelector('script[type="application/json"][data-variants-catalog]');
    if (!script?.textContent) return null;

    try {
      const parsed = JSON.parse(script.textContent);
      this.#variantsCatalog = Array.isArray(parsed) ? parsed : null;
    } catch {
      this.#variantsCatalog = null;
    }

    return this.#variantsCatalog;
  }

  /**
   * Selected value keyed by each fieldset's true product-option position
   * (`data-fieldset-index`, set from `forloop.index0` over ALL options in
   * ea-render-variant-option.liquid). An option with only one value renders
   * as static text with no fieldset at all — skipping it must not shift
   * later fieldsets' positions, so array order alone can't be used here.
   * @returns {Array<{position: number, value: string | null}>}
   */
  #getSelectedOptionValuesByPosition() {
    const fieldsets = this.#getSortableFieldsets();

    return fieldsets
      .map((fieldset) => {
        const position = Number(fieldset.dataset.fieldsetIndex);
        if (!Number.isInteger(position)) return null;

        const checkedRadio = fieldset.querySelector('input[type="radio"]:checked');
        if (checkedRadio instanceof HTMLInputElement) {
          return { position, value: checkedRadio.value || null };
        }

        const select = fieldset.querySelector('select.variant-option__select');
        if (select instanceof HTMLSelectElement) {
          const option = select.options[select.selectedIndex];
          return { position, value: option?.value || null };
        }

        return { position, value: null };
      })
      .filter((entry) => entry !== null);
  }

  /**
   * Whether every option row has an explicit user selection.
   * @returns {boolean}
   */
  #hasCompleteSelection() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    if (fieldsets.length === 0) return true;

    return fieldsets.every((fieldset) => {
      const select = fieldset.querySelector('select.variant-option__select');
      if (select instanceof HTMLSelectElement) {
        const option = select.options[select.selectedIndex];
        return Boolean(option?.value && this.#isOptionAvailable(option));
      }

      return Boolean(fieldset.querySelector('input[type="radio"]:checked'));
    });
  }

  /**
   * @param {HTMLSelectElement} select
   * @returns {boolean}
   */
  #ensureSelectableSelectOption(select) {
    const selected = select.options[select.selectedIndex];
    if (!selected?.value) {
      return false;
    }

    if (selected && this.#isOptionAvailable(selected)) {
      return false;
    }

    const firstAvailable = Array.from(select.options).find((option) => this.#isOptionAvailable(option));
    if (!firstAvailable) {
      return false;
    }

    select.value = firstAvailable.value;
    this.updateSelectedOption(select);
    return true;
  }

  #dispatchSelectionChange() {
    const selected = this.selectedOption;

    if (selected instanceof HTMLInputElement) {
      selected.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }

    if (
      selected instanceof HTMLOptionElement &&
      selected.parentElement instanceof HTMLSelectElement
    ) {
      selected.parentElement.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  /**
   * @param {EventTarget | null} target
   */
  #revertUnavailableSelection(target) {
    if (target instanceof HTMLSelectElement) {
      this.#ensureSelectableSelectOption(target);
      return;
    }

    if (!(target instanceof HTMLInputElement)) return;

    const fieldsetIndex = this.#compactFieldsetIndex(target.closest('fieldset'));
    const radios = this.#radios[fieldsetIndex] ?? [];
    const checkedIndices = this.#checkedIndices[fieldsetIndex];
    const fallbackIndex = checkedIndices?.[1] ?? checkedIndices?.[0];

    const fallback =
      (fallbackIndex !== undefined && radios[fallbackIndex] && this.#isOptionAvailable(radios[fallbackIndex])
        ? radios[fallbackIndex]
        : undefined) ?? radios.find((radio) => this.#isOptionAvailable(radio));

    target.checked = false;
    target.dataset.currentChecked = 'false';

    if (fallback instanceof HTMLInputElement) {
      fallback.checked = true;
      this.updateSelectedOption(fallback);
    }
  }

  /**
   * Swaps the modal gallery image immediately when a color is chosen (before section fetch).
   * @param {HTMLElement} selectedOption
   */
  #previewQuickAddModalImage(selectedOption) {
    if (!(selectedOption instanceof HTMLInputElement)) return;

    const cardImage = selectedOption
      .closest('.variant-option__ea-card')
      ?.querySelector('.variant-option__ea-card-image');

    if (!(cardImage instanceof HTMLImageElement)) return;

    const thumbSrc = cardImage.currentSrc || cardImage.src;
    if (!thumbSrc) return;

    const src = thumbSrc.replace(/([?&])width=\d+/i, '$1width=1200');

    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;

    const img =
      modalContent.querySelector(
        '.product-information__media slideshow-slide:not([aria-hidden="true"]) img, .product-information__media slideshow-slide:first-child img'
      ) ?? modalContent.querySelector('.product-information__media img');

    if (!(img instanceof HTMLImageElement)) return;

    const separator = src.includes('?') ? '&' : '?';
    const nextSrc = src.includes('width=') ? src : `${src}${separator}width=1200`;

    img.src = nextSrc;
    img.removeAttribute('srcset');
    img.removeAttribute('sizes');
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
